import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import {applyPlugin } from 'jspdf-autotable';
import { useNotificationContext } from '../contexts/NotificationContext';
applyPlugin(jsPDF);

const PDFEnseignants = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [grades, setGrades] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPdf, setCurrentPdf] = useState(null);
  
  const [filters, setFilters] = useState({
    department: '',
    academicYear: '',
    semester: 1,
    grade: '' // Filtre optionnel par grade
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [deptRes, yearRes, gradeRes] = await Promise.all([
        axios.get('/api/departments'),
        axios.get('/api/academic-years'),
        axios.get('/api/grades')
      ]);
      setDepartments(deptRes.data);
      setAcademicYears(yearRes.data);
      setGrades(gradeRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const generatePDF = async () => {
    if (!filters.department || !filters.academicYear) {
      showError('Veuillez sélectionner le département et l\'année universitaire');
      return;
    }

    setLoading(true);
    try {
      const teachersWithSessions = await fetchTeachersWithSessions();
      const pdf = createPDF(teachersWithSessions);
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setCurrentPdf(pdf);
      showSuccess('PDF généré avec succès');
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      showError('Erreur lors de la génération du PDF');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    if (currentPdf) {
      currentPdf.save(`liste-enseignants-matieres-${Date.now()}.pdf`);
    }
  };

  const fetchTeachersWithSessions = async () => {
    try {
      // Récupérer les sessions pour le semestre
      const sessionsRes = await axios.get(`/api/sessions?semester=${filters.semester}&academicYear=${filters.academicYear}&department=${filters.department}&limit=10000`);
      const sessions = sessionsRes.data;

      // Récupérer tous les enseignants
      const teachersRes = await axios.get('/api/teachers');
      let allTeachers = teachersRes.data;

      // Filtrer par département
      allTeachers = allTeachers.filter(teacher => 
        teacher.departments?.some(dept => dept._id === filters.department)
      );

      // Filtrer par grade si spécifié
      if (filters.grade) {
        allTeachers = allTeachers.filter(teacher => teacher.grade?._id === filters.grade);
      }

      // Ne garder que les enseignants ayant au moins une séance et calculer leurs statistiques
      const teachersWithSessions = allTeachers.filter(teacher =>
        sessions.some(session => session.teacher?._id === teacher._id)
      ).map(teacher => {
        const teacherSessions = sessions.filter(s => s.teacher?._id === teacher._id);
        const subjects = [...new Set(teacherSessions.map(s => s.course?.name).filter(Boolean))];
        
        // Calculer les heures par type selon la nouvelle règle:
        // Séance LECTURE : ajouter course.hours.lectures au cours
        // Séance INTEGRATED : ajouter course.hours.lectures au cours et course.hours.tutorials au TD
        // Séance PRACTICAL : ajouter 1.5h au TP
        let totalHours = 0, coursHours = 0, tdHours = 0, tpHours = 0;
        
        teacherSessions.forEach(session => {
          const course = session.course;
          if (session.type === 'LECTURE') {
            // Séance cours : Pour le moment type séance cours == CI
            //Ajouter 1h cours et 0.5 de td
            const lectureHours = course?.hours?.lectures || 1;
            const tutorialHours = course?.hours?.tutorials || 0.5;
            coursHours += lectureHours;
            tdHours += tutorialHours;
            totalHours += lectureHours + tutorialHours;
          } else if (session.type === 'INTEGRATED') {
            // Séance intégrée : ajouter cours ET TD
            const lectureHours = course?.hours?.lectures || 1;
            const tutorialHours = course?.hours?.tutorials || 0.5;
            coursHours += lectureHours;
            tdHours += tutorialHours;
            totalHours += lectureHours + tutorialHours;
          } else if (session.type === 'PRACTICAL') {
            // Séance TP : 1.5h TP
            tpHours += 1.5;
            totalHours += 1.5;
          } else if (session.type === 'TUTORIAL') {
            // Séance TD : ajouter les heures de TD définies
            const tutorialHours = course?.hours?.tutorials || 1.5; // Fallback si pas défini
            tdHours += tutorialHours;
            totalHours += tutorialHours;
          }
        });
        
        return {
          ...teacher,
          subjects,
          totalHours,
          coursHours,
          tdHours,
          tpHours
        };
      });

      return teachersWithSessions;
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      return [];
    }
  };

  const createPDF = (teachersData) => {
    const doc = new jsPDF('l', 'mm', 'a4'); // Format paysage
    
    doc.setFontSize(16);
    doc.text('Liste des Enseignants avec Matières Enseignées', 20, 20);
    
    const selectedDept = departments.find(d => d._id === filters.department);
    const selectedYear = academicYears.find(y => y._id === filters.academicYear);
    
    doc.setFontSize(12);
    doc.text(`Département: ${selectedDept?.name || ''}`, 20, 35);
    doc.text(`Année: ${selectedYear?.name || ''} - Semestre ${filters.semester}`, 20, 45);
    
    if (filters.grade) {
      const selectedGrade = grades.find(g => g._id === filters.grade);
      doc.text(`Grade: ${selectedGrade?.name || ''}`, 20, 55);
    }

    // Préparer les données du tableau
    const tableData = teachersData.map((teacher, index) => [
      index + 1,
      `${teacher.firstName} ${teacher.lastName}`,
      teacher.grade?.name || '',
      teacher.subjects.join(', '),
      teacher.totalHours.toFixed(1),
      teacher.coursHours.toFixed(1),
      teacher.tdHours.toFixed(1),
      teacher.tpHours.toFixed(1)
    ]);

    doc.autoTable({
      head: [['N°', 'Nom Complet', 'Grade', 'Matières Enseignées', 'Total H', 'Cours H', 'TD H', 'TP H']],
      body: tableData,
      startY: filters.grade ? 65 : 55,
      tableWidth: 'wrap',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [102, 126, 234], fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 45 },
        2: { cellWidth: 35 },
        3: { cellWidth: 80 },
        4: { cellWidth: 25, halign: 'center' },
        5: { cellWidth: 25, halign: 'center' },
        6: { cellWidth: 25, halign: 'center' },
        7: { cellWidth: 25, halign: 'center' }
      }
    });

    // Calculer et afficher les totaux
    const totalTeachers = teachersData.length;
    const totalCoursHours = teachersData.reduce((sum, t) => sum + t.coursHours, 0);
    const totalTdHours = teachersData.reduce((sum, t) => sum + t.tdHours, 0);
    const totalTpHours = teachersData.reduce((sum, t) => sum + t.tpHours, 0);
    // Recalculer le total correct
    const totalAllHours = totalCoursHours + totalTdHours + totalTpHours;

    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(`Total: ${totalTeachers} enseignant(s)`, 20, finalY);
    doc.text(`Total Heures: ${totalAllHours.toFixed(1)}h (Cours: ${totalCoursHours.toFixed(1)}h, TD: ${totalTdHours.toFixed(1)}h, TP: ${totalTpHours.toFixed(1)}h)`, 20, finalY + 10);

    return doc;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Génération PDF - Liste des Enseignants</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
              <select 
                name="department" 
                value={filters.department} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Année universitaire</label>
              <select 
                name="academicYear" 
                value={filters.academicYear} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Semestre</label>
              <select 
                name="semester" 
                value={filters.semester} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={1}>Semestre 1</option>
                <option value={2}>Semestre 2</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grade (optionnel)</label>
              <select 
                name="grade" 
                value={filters.grade} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Tous les grades</option>
                {grades.map(grade => (
                  <option key={grade._id} value={grade._id}>{grade.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={generatePDF} 
              disabled={loading || !filters.department || !filters.academicYear}
              className={`px-6 py-3 text-sm font-medium text-white rounded-lg focus:ring-2 focus:ring-primary-500 ${
                loading || !filters.department || !filters.academicYear
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Génération...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  👨🏫 Générer PDF
                </span>
              )}
            </button>
            
            {pdfUrl && (
              <button 
                onClick={downloadPDF}
                className="px-6 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500"
              >
                <span className="flex items-center justify-center gap-2">
                  💾 Télécharger PDF
                </span>
              </button>
            )}
          </div>
        </div>

        {pdfUrl && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">👁️ Aperçu du document</h3>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <iframe 
                src={pdfUrl}
                className="w-full h-96"
                title="Aperçu PDF"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFEnseignants;